import type { ExportPresetId, ExportSetup, ExportTextStyle } from '../../types/exportSetup'

// Standard formats for DOCX and ODT exports. They replace the editor's
// on-screen settings (size, spacing, colors), which writers choose for comfort
// while drafting, with the conventions the finished document is expected to
// follow. The body typeface stays the writer's own unless a format is defined
// by its typeface. All measurements are in points and millimeters.

export type PaperSize = 'letter' | 'a4'

export interface ExportPreset {
  id: ExportPresetId
  name: string
  description: string
  /** A short line of the key specifications, shown on the card. */
  specs: string
  /** Formats with a fixed page size ignore the paper choice. */
  usesPaperChoice: boolean
  /** The typeface the format requires, or null to use the writer's font. */
  typeface: string | null
  build: (font: string, paper: PaperSize) => ExportSetup
}

const INCH = 25.4
const INK = '#000000'

function text(font: string, overrides: Partial<ExportTextStyle> = {}): ExportTextStyle {
  return {
    fontFamily: font, fontSize: 12, fontWeight: 400, color: INK,
    alignment: 'left', lineHeight: 1, spaceAfter: 0, letterSpacing: 0,
    textCase: 'none', ...overrides,
  }
}

/** Shared by every format; each one overrides what it defines. */
function base(font: string, paper: PaperSize): ExportSetup {
  return {
    mode: 'custom', includeTitle: true,
    title: text(font), sectionTitle: text(font), body: text(font), headings: text(font),
    paragraphIndent: 0, pageSize: paper, orientation: 'portrait', margins: INCH,
    backgroundColor: '#ffffff',
    keepInlineEmphasis: true, keepInlineFonts: false, keepInlineSizes: false,
    keepInlineColors: false, keepHighlights: false, keepAlignment: true,
    widowOrphanControl: true, pageBreakBeforeTitle: false,
    indentFirstParagraph: true, sceneBreak: 'asterisks',
    pageNumbers: 'none', pageNumberTitle: false, pageNumberOnFirstPage: true,
  }
}

/** Standard manuscript format for submitting fiction and nonfiction. */
function manuscript(font: string, paper: PaperSize, id: ExportPresetId): ExportSetup {
  const doubled = { lineHeight: 2 }
  return {
    ...base(font, paper),
    preset: id,
    title: text(font, { ...doubled, alignment: 'center', spaceAfter: 24 }),
    sectionTitle: text(font, { ...doubled, alignment: 'center', spaceAfter: 24 }),
    headings: text(font, { ...doubled, fontWeight: 700, alignment: 'center' }),
    body: text(font, doubled),
    paragraphIndent: INCH / 2,
    pageBreakBeforeTitle: true,
    sceneBreak: 'hash',
    pageNumbers: 'header',
    pageNumberTitle: true,
    pageNumberOnFirstPage: false,
  }
}

export const EXPORT_PRESETS: ExportPreset[] = [
  {
    id: 'manuscript',
    name: 'Standard Manuscript',
    description: 'For submitting novels and stories to agents, editors, and publishers.',
    specs: '12 pt · Double-spaced · ½″ indents · # scene breaks · Title and page in the header',
    usesPaperChoice: true,
    typeface: null,
    build: (font, paper) => manuscript(font, paper, 'manuscript'),
  },
  {
    id: 'manuscript-courier',
    name: 'Classic Manuscript',
    description: 'The traditional typewriter look, still preferred by some editors and magazines.',
    specs: 'Courier Prime · 12 pt · Double-spaced · ½″ indents · # scene breaks',
    usesPaperChoice: true,
    typeface: 'Courier Prime',
    build: (_font, paper) => manuscript('Courier Prime', paper, 'manuscript-courier'),
  },
  {
    id: 'document',
    name: 'Clean Document',
    description: 'A modern word-processor layout for sharing drafts with readers and collaborators.',
    specs: '12 pt · 1.15 spacing · Space between paragraphs · Page numbers in the footer',
    usesPaperChoice: true,
    typeface: null,
    build: (font, paper) => ({
      ...base(font, paper),
      preset: 'document',
      title: text(font, { fontSize: 20, fontWeight: 600, lineHeight: 1.15, spaceAfter: 12 }),
      sectionTitle: text(font, { fontSize: 16, fontWeight: 600, lineHeight: 1.15, spaceAfter: 8 }),
      headings: text(font, { fontSize: 14, fontWeight: 600, lineHeight: 1.15, spaceAfter: 6 }),
      body: text(font, { lineHeight: 1.15, spaceAfter: 8 }),
      // Deliberate inline choices are part of a shared draft.
      keepInlineFonts: true, keepInlineSizes: true, keepInlineColors: true, keepHighlights: true,
      pageNumbers: 'footer',
    }),
  },
  {
    id: 'academic',
    name: 'Academic Paper',
    description: 'Double-spaced with bold centered headings, in the style of APA and MLA papers.',
    specs: '12 pt · Double-spaced · ½″ indents · Page numbers in the header',
    usesPaperChoice: true,
    typeface: null,
    build: (font, paper) => ({
      ...base(font, paper),
      preset: 'academic',
      title: text(font, { fontWeight: 700, lineHeight: 2, alignment: 'center' }),
      sectionTitle: text(font, { fontWeight: 700, lineHeight: 2, alignment: 'center' }),
      headings: text(font, { fontWeight: 700, lineHeight: 2, alignment: 'center' }),
      body: text(font, { lineHeight: 2 }),
      paragraphIndent: INCH / 2,
      pageNumbers: 'header',
    }),
  },
  {
    id: 'book-proof',
    name: 'Book Proof',
    description: 'A 6 × 9 trade paperback page for proofreading, beta readers, and print-on-demand proofs.',
    specs: '6 × 9″ · 11 pt · Justified · Flush first paragraphs · Chapters on new pages',
    usesPaperChoice: false,
    typeface: null,
    build: (font, paper) => ({
      ...base(font, paper),
      preset: 'book-proof',
      pageSize: 'six-nine',
      margins: 0.75 * INCH,
      title: text(font, { fontSize: 18, lineHeight: 1.2, alignment: 'center', spaceAfter: 36 }),
      sectionTitle: text(font, { fontSize: 14, lineHeight: 1.2, alignment: 'center', spaceAfter: 24, textCase: 'uppercase' }),
      headings: text(font, { fontSize: 11, fontWeight: 600, lineHeight: 1.35, alignment: 'center', spaceAfter: 6 }),
      body: text(font, { fontSize: 11, lineHeight: 1.35, alignment: 'justify' }),
      paragraphIndent: INCH / 4,
      keepInlineFonts: true,
      indentFirstParagraph: false,
      pageBreakBeforeTitle: true,
      pageNumbers: 'footer',
      pageNumberOnFirstPage: false,
    }),
  },
  {
    id: 'screenplay',
    name: 'Screenplay',
    description: 'Courier on a script page. Sets type and margins; dialogue and character indents are not applied.',
    specs: 'Courier Prime · 12 pt · 1½″ left margin · Uppercase scene headings',
    usesPaperChoice: true,
    typeface: 'Courier Prime',
    build: (_font, paper) => {
      const font = 'Courier Prime'
      return {
        ...base(font, paper),
        preset: 'screenplay',
        title: text(font, { alignment: 'center', spaceAfter: 24, textCase: 'uppercase' }),
        sectionTitle: text(font, { spaceAfter: 12, textCase: 'uppercase' }),
        headings: text(font, { spaceAfter: 12, textCase: 'uppercase' }),
        body: text(font, { spaceAfter: 12 }),
        marginLeft: 1.5 * INCH,
        sceneBreak: 'blank',
        pageNumbers: 'header',
        pageNumberOnFirstPage: false,
      }
    },
  },
]

export function findExportPreset(id: string | null | undefined): ExportPreset | undefined {
  return EXPORT_PRESETS.find(preset => preset.id === id)
}

/** Letter in the Americas and the Philippines, A4 almost everywhere else. */
export function defaultPaperSize(): PaperSize {
  const locale = typeof navigator !== 'undefined' ? navigator.language || '' : ''
  const region = locale.split(/[-_]/)[1]?.toUpperCase()
  return region && ['US', 'CA', 'MX', 'PH', 'CL', 'CO', 'VE', 'PR'].includes(region) ? 'letter' : 'a4'
}
